import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import TopicListCard from '@/components/feature/TopicListCard';
import { searchTopics, sortSearchResults, getHotTopics, type SearchSort } from '@/lib/community';
import { hotKeywords } from '@/mocks/activity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn } from '@/lib/format';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<SearchSort>('relevance');

  useEffect(() => {
    setInput(query);
  }, [query]);

  const rawResults = useMemo(() => searchTopics(query), [query]);
  const results = useMemo(() => sortSearchResults(rawResults, query, sort), [rawResults, query, sort]);
  const hotTopics = getHotTopics(4);

  usePageMeta({
    title: query ? `搜索「${query}」 - 蜜糖社区` : '搜索话题 - 蜜糖社区',
    description: query
      ? `蜜糖社区关于「${query}」的搜索结果，共 ${results.length} 条相关内容，涵盖话题、经验分享与讨论。`
      : '在蜜糖社区搜索你感兴趣的话题、关键词或作者，快速找到想看的讨论内容。',
    keywords: query ? `${query},蜜糖社区搜索,话题搜索,相关讨论` : '蜜糖社区,话题搜索,关键词搜索',
  });

  const submit = (kw: string) => {
    const value = kw.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
    setSort('relevance');
  };

  return (
    <Layout>
      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

        <h1 className="mt-5 text-2xl md:text-3xl font-extrabold text-foreground-900">搜索话题</h1>

        {/* Search box */}
        <form
          onSubmit={(e) => { e.preventDefault(); submit(input); }}
          className="mt-5 flex items-center gap-2 bg-background-50 border border-background-200 rounded-lg p-2"
        >
          <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
            <i className="ri-search-line text-lg" />
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入关键词，如「阳台」「露营」「舒芙蕾」…"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none py-1.5"
          />
          <button
            type="submit"
            className="rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        {/* Hot keywords */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门：</span>
          {hotKeywords.slice(0, 6).map((kw) => (
            <button
              key={kw}
              type="button"
              onClick={() => submit(kw)}
              className="rounded-full bg-background-100 border border-background-200 px-3 py-1 text-xs font-label font-bold text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {kw}
            </button>
          ))}
        </div>

        {query ? (
          <>
            {/* Result meta + sort */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-background-50 border border-background-200 rounded-lg p-3">
              <p className="text-sm text-foreground-600 px-1">
                关键词 <span className="font-label font-bold text-foreground-900">「{query}」</span> 共搜索到
                <span className="font-label font-bold text-primary-700"> {results.length} </span>条结果
              </p>
              <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
                {([['relevance', '按相关度'], ['time', '按时间']] as [SearchSort, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSort(key)}
                    className={cn(
                      'rounded-full px-4 py-1.5 text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                      sort === key ? 'bg-background-50 text-primary-700' : 'text-secondary-700 hover:text-secondary-900',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="mt-5">
              {results.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {results.map((topic) => (
                    <TopicListCard key={topic.id} topic={topic} />
                  ))}
                </div>
              ) : (
                <div>
                  <EmptyState
                    icon="ri-search-eye-line"
                    title={`没有找到与「${query}」相关的内容`}
                    description="换个关键词试试，或者看看下面大家都在聊的热门话题。"
                    actionLabel="清空搜索"
                    onAction={() => { setInput(''); setSearchParams({}); }}
                  />
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-fire-line text-accent-600" />
                      <h2 className="text-lg font-extrabold text-foreground-900">试试这些热门话题</h2>
                    </div>
                    <div className="flex flex-col gap-3">
                      {hotTopics.map((topic) => (
                        <TopicListCard key={topic.id} topic={topic} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-8">
            <div className="text-center py-10 mb-8 bg-background-50 border border-dashed border-background-300 rounded-lg">
              <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-2-line text-3xl" />
              </span>
              <h2 className="text-lg font-bold text-foreground-900">开始你的探索</h2>
              <p className="mt-2 text-sm text-foreground-500">在上方输入关键词，找到志同道合的讨论。</p>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <i className="ri-fire-line text-accent-600" />
              <h2 className="text-lg font-extrabold text-foreground-900">大家都在看</h2>
            </div>
            <div className="flex flex-col gap-3">
              {hotTopics.map((topic) => (
                <TopicListCard key={topic.id} topic={topic} />
              ))}
            </div>

            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-price-tag-3-line text-primary-600" />
                <h2 className="text-lg font-extrabold text-foreground-900">按分类浏览</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {['life', 'entertainment', 'culture', 'tech', 'food', 'travel'].map((id) => (
                  <CategoryLink key={id} id={id} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function CategoryLink({ id }: { id: string }) {
  const map: Record<string, { name: string; icon: string }> = {
    life: { name: '生活', icon: 'ri-home-smile-2-line' },
    entertainment: { name: '娱乐', icon: 'ri-movie-2-line' },
    culture: { name: '文化', icon: 'ri-book-open-line' },
    tech: { name: '技术', icon: 'ri-code-s-slash-line' },
    food: { name: '美食', icon: 'ri-restaurant-2-line' },
    travel: { name: '旅行', icon: 'ri-flight-takeoff-line' },
  };
  const item = map[id];
  if (!item) return null;
  return (
    <Link
      to={`/category/${id}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label font-bold text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className={cn(item.icon, 'text-primary-600')} />
      {item.name}
    </Link>
  );
}